import { useEffect, useState } from 'react';
import { Stack, Typography, Alert, Box } from '@mui/material';
import { useDataProvider } from 'react-admin';
import { useFeature } from '../featureFlags/FeatureGate';
import { OPERATIONAL_LABEL } from '../fleet/fleetFeatures';
import { DASHBOARD_RESOURCE } from './resource';
import { Tile, TileGrid } from './Tile';
import { buildListUrl } from './listUrl';
import { fmtDateTimeShort } from '../../shared/listCells';
import { useSessionReady } from '../../hooks/useSessionReady';
import { STATUS_CARRIER_ASSIGNED } from '../userOffers/planningStatus';
import { MARKERS_FILTER_LOST_VEHICLE } from '../userOffers/markers';
import { PermissionGate } from '../PermissionGate';
import { ELEMENT } from '../roleCenter/elementKeys';
import { MOCKUP_CLASS } from '../../mockupLook/tokens';

interface DashboardSummary {
  zlecenia: {
    toPlan: number;
    prePlanned: number;
    assigned: number;
    atRisk: number;
    overdue: number;
    idle: number;
    /** Czerwona lista — zlecenia, które straciły pojazd (01.10.2026). */
    lostVehicle?: number;
  };
  fleet: {
    onOrder: number;
    available: number;
    outOfService: number;
    withoutDriver: number;
  };
  external: {
    handedToCarrier: number;
    carriersOnFile: number;
  };
  dataGaps: {
    ordersWithUnknownVehicle: number;
    vehiclesWithoutCompany: number;
    driversWithoutVehicle: number;
  };
  computedAt: string;
}

/**
 * Komunikat, gdy pulpitu nie da się wczytać. Stały tekst zamiast komunikatu
 * błędu: ten bywał techniczny („Request failed with status code 500”) albo
 * był surowym kluczem słownika („ra.message.forbidden” przy 403) — poprawa
 * brzmienia 01.10.2026.
 */
export const DASHBOARD_LOAD_ERROR = 'Nie udało się wczytać pulpitu. Odśwież stronę.';

/**
 * Dashboard planisty — ekran startowy.
 *
 * Trzy sekcje odpowiadają trzem różnym pytaniom: co mam dziś do zrobienia,
 * czym dysponuję i gdzie dane są niekompletne. Ostatnia sekcja jest osobno
 * świadomie — to nie jest praca planisty, tylko sygnał dla nas i dla
 * klienta, gdzie kartoteki się nie domykają.
 *
 * Kafelki prowadzące do sekcji działu widzi tylko rola z uprawnieniem
 * (01.10.2026): flota — planowanie FTL, kartoteka przewoźników — Trade.
 * Inaczej kafelek prowadziłby do ekranu, który odpowie 403.
 *
 * Napisy kafelków — poprawa brzmienia 01.10.2026: nazwy jak stany na liście
 * zleceń („Gotowe do planowania”, „Bezczynne”), opisy bez kalek („oznaczone
 * ryzykiem”, „nikt nie działa dłużej niż próg stanu”).
 */
export const Dashboard = () => {
  const dataProvider = useDataProvider();
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const ready = useSessionReady();
  // „W eksploatacji” zamiast „sprawne” — przełącznik (12.10.2026).
  const operationalLabel = useFeature(OPERATIONAL_LABEL);

  useEffect(() => {
    // Bez sesji nie pytamy — react-admin potrafi zamontować pulpit, zanim
    // sprawdzi logowanie, a zapytanie bez tokena kończyło się 401.
    if (!ready) return undefined;
    let active = true;
    // Dashboard nie jest zasobem react-admina (jeden obiekt, nie lista), więc
    // sięgamy po niego wprost przez dostawcę danych. Nazwa zasobu = ścieżka
    // kontrolera; trzyma ją stała, bo literał w wywołaniu przeżył
    // przemianowanie tras i ekran przestał się ładować (404).
    (dataProvider as unknown as {
      getOne: (z: string, p: { id: string }) => Promise<{ data: DashboardSummary }>;
    })
      .getOne(DASHBOARD_RESOURCE, { id: '' })
      .then(({ data }) => {
        if (active) setSummary(data);
      })
      .catch(() => {
        if (active) setLoadError(DASHBOARD_LOAD_ERROR);
      });
    return () => {
      active = false;
    };
  }, [dataProvider, ready]);

  const z = summary?.zlecenia;
  const f = summary?.fleet;
  const n = summary?.external;
  const d = summary?.dataGaps;
  const loading = !summary && !loadError;

  return (
    <Stack spacing={3} sx={{ p: 2 }}>
      {loadError && <Alert severity="error">{loadError}</Alert>}

      <Box>
        <Typography variant="h6" gutterBottom className={MOCKUP_CLASS.sectionTitle}>
          Zlecenia
        </Typography>
        <TileGrid>
          <Tile
            label="Gotowe do planowania"
            value={z?.toPlan}
            description="czekają na wskazanie pojazdu"
            linkTo={buildListUrl('user-offers', { planningStatus: 'ready' })}
            loading={loading}
          />
          <Tile
            label="Wstępnie planowane"
            value={z?.prePlanned}
            description="mają wstępnie wskazane pojazdy i czekają na decyzję"
            linkTo={buildListUrl('user-offers', { planningStatus: 'tentative' })}
            loading={loading}
          />
          <Tile
            label="Zagrożone"
            value={z?.atRisk}
            description="ze zgłoszonym ryzykiem"
            linkTo={buildListUrl('user-offers', { atRisk: true })}
            highlight="ostrzezenie"
            loading={loading}
          />
          <Tile
            label="Po terminie ważności"
            value={z?.overdue}
            description="termin minął, a decyzji wciąż brak"
            linkTo={buildListUrl('user-offers', { wygasle: true })}
            highlight="ostrzezenie"
            loading={loading}
          />
          <Tile
            label="Bezczynne"
            value={z?.idle}
            description="nikt się nimi nie zajął w wyznaczonym czasie"
            linkTo={buildListUrl('user-offers', { idle: true })}
            highlight="ostrzezenie"
            loading={loading}
          />
          {/* Czerwona lista (omówienie 01.10.2026) — ten sam filtr „Znaczniki”
              co na liście, więc liczba zgadza się z widokiem po kliknięciu. */}
          <Tile
            label="Straciły pojazd"
            value={z?.lostVehicle}
            description="było obsadzone, a pojazd odpięto — czekają na ponowne obsadzenie"
            linkTo={buildListUrl('user-offers', { forklift: MARKERS_FILTER_LOST_VEHICLE })}
            highlight="ostrzezenie"
            loading={loading}
          />
        </TileGrid>
      </Box>

      <PermissionGate permission="FTL_PLANNING" element={ELEMENT.dashboardFleet}>
        <Box>
          <Typography variant="h6" gutterBottom className={MOCKUP_CLASS.sectionTitle}>
            Flota
          </Typography>
          <TileGrid>
            <Tile
              label="Pojazdy pod zlecenie"
              value={f?.onOrder}
              description="bez naczep i wózków"
              linkTo={buildListUrl('cars', { ciagniki: true })}
              loading={loading}
            />
            <Tile
              label={operationalLabel ? 'Dostępne i w eksploatacji' : 'Dostępne i sprawne'}
              value={f?.available}
              linkTo={buildListUrl('cars', {
                ciagniki: true,
                available: true,
                operational: true,
              })}
              loading={loading}
            />
            <Tile
              label="Poza eksploatacją"
              value={f?.outOfService}
              description="wycofane z ruchu według TMS"
              linkTo={buildListUrl('cars', { operational: false })}
              highlight="ostrzezenie"
              loading={loading}
            />
            <Tile
              label="Bez kierowcy"
              value={f?.withoutDriver}
              description="spośród pojazdów pod zlecenie"
              linkTo={buildListUrl('cars', { ciagniki: true, zKierowca: false })}
              loading={loading}
            />
          </TileGrid>
        </Box>
      </PermissionGate>

      <Box>
        <Typography variant="h6" gutterBottom className={MOCKUP_CLASS.sectionTitle}>
          Realizacja zewnętrzna
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Zlecenia realizowane przez przewoźników. Ich pojazdów nie ma w naszej
          flocie — to normalne, a nie brak danych.
        </Typography>
        <TileGrid>
          {/* Nazwa, liczba i link mówią o tym samym stanie (28.09.2026:
              kafelek liczył każde zlecenie z przewoźnikiem, a prowadził do
              banku bez filtra). */}
          <Tile
            label="Obsadzone przewoźnikiem"
            value={n?.handedToCarrier}
            description="przewoźnik wpisany w TMS"
            linkTo={buildListUrl('user-offers', { planningStatus: STATUS_CARRIER_ASSIGNED })}
            loading={loading}
          />
          <PermissionGate permission="TRADE" element={ELEMENT.dashboardCarriersOnFile}>
            <Tile
              label="Przewoźnicy w kartotece"
              value={n?.carriersOnFile}
              // Cała kartoteka, jak licznik w API — z filtrem „ze zleceniami”
              // lista pokazywała mniej niż kafelek (582 wobec 587).
              linkTo={buildListUrl('carriers')}
              loading={loading}
            />
          </PermissionGate>
        </TileGrid>
      </Box>

      <Box>
        <Typography variant="h6" gutterBottom className={MOCKUP_CLASS.sectionTitle}>
          Dane do uzupełnienia
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Miejsca, w których kartoteki w TMS nie zgadzają się ze zleceniami.
          To nie jest zadanie planisty — to sygnał, co trzeba wyjaśnić lub
          uzupełnić w TMS.
        </Typography>
        <TileGrid>
          {/* Kafelek liczy zlecenia, nie pojazdy — nazwa mówi to wprost.
              Prowadzi do banku z filtrem tej samej reguły co licznik
              (13.10.2026: wcześniej był bez odnośnika). */}
          <Tile
            label="Zlecenia z nieznanym pojazdem"
            value={d?.ordersWithUnknownVehicle}
            description="numer pojazdu spoza floty, bez przewoźnika"
            linkTo={buildListUrl('user-offers', { unknownVehicle: true })}
            highlight="uwaga"
            loading={loading}
          />
          <PermissionGate permission="FTL_PLANNING" element={ELEMENT.dashboardFleetDataGaps}>
            <Tile
              label="Pojazdy bez spółki"
              value={d?.vehiclesWithoutCompany}
              description="brak spółki w kartotece TMS"
              linkTo={buildListUrl('cars', { bezSpolki: true })}
              highlight="uwaga"
              loading={loading}
            />
            <Tile
              label="Kierowcy bez pojazdu"
              value={d?.driversWithoutVehicle}
              linkTo={buildListUrl('drivers', { zPojazdem: false })}
              highlight="uwaga"
              loading={loading}
            />
          </PermissionGate>
        </TileGrid>
      </Box>

      {summary && (
        <Typography variant="caption" color="text.secondary">
          Stan na {fmtDateTimeShort(summary.computedAt)}
        </Typography>
      )}
    </Stack>
  );
};
